import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { AgentTraffic } from "@/components/AgentTraffic";
import { getAnalytics, resolveKnowledgeGap } from "@/lib/analytics.functions";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  CartesianGrid,
} from "recharts";
import { BrainCircuit, Check } from "lucide-react";
import { useHydratedTranslation } from "@/components/marketing/Layout";
import { toast } from "sonner";

import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/_authenticated/analytics")({
  head: () =>
    buildSeo({
      path: "/analytics",
      title: "Analytics — Reality Twin",
      description:
        "Track Twin performance across conversations, messages and captured leads — with trends, top pages and lead-quality signals.",
      noindex: true,
    }),
  component: Page,
});

function Page() {
  const t = useHydratedTranslation();
  const fetchAnalytics = useServerFn(getAnalytics);
  const resolveGap = useServerFn(resolveKnowledgeGap);
  const [days, setDays] = useState(30);
  const [data, setData] = useState<any>({ series: [], top: [], gaps: [] });

  useEffect(() => {
    fetchAnalytics({ data: { days } }).then(setData).catch(console.error);
  }, [fetchAnalytics, days]);

  async function markResolved(id: string) {
    try {
      await resolveGap({ data: { id } });
      setData((d: any) => ({ ...d, gaps: (d.gaps ?? []).filter((g: any) => g.id !== id) }));
      toast.success(t("app.pages.analytics.coveredToast"));
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  const series = (data.series ?? []).map((r: any) => ({
    d: new Date(r.day).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    conversations: Number(r.conversations),
    messages: Number(r.messages),
    leads: Number(r.leads),
  }));

  /**
   * Money that actually moved through the platform.
   *
   * Only paid jobs count. Open quotes are shown apart, never folded into the
   * total — an owner has to be able to reconcile this against their bank.
   */
  function RevenueBlock({ revenue, days }: { revenue: any; days: number }) {
    if (!revenue) return null;
    const rows = revenue.byCurrency ?? [];
    if (rows.length === 0 && !revenue.pendingQuotes) return null;

    return (
      <div className="glass-strong rounded-2xl p-5 mb-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-3">
          {t("app.pages.analytics.revenueTitle", {
            defaultValue: "Earned through Reality Twin · last {{days}} days",
            days,
          })}
        </div>

        {rows.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            {t("app.pages.analytics.revenueNone", {
              defaultValue: "No jobs have been paid through the platform yet.",
            })}
          </div>
        ) : (
          <div className="flex flex-wrap gap-8">
            {rows.map((r: any) => (
              <div key={r.currency}>
                <div className="font-display text-3xl">
                  {(r.netCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}{" "}
                  <span className="text-base text-muted-foreground">{r.currency}</span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {t("app.pages.analytics.revenueNet", {
                    defaultValue: "yours, after the {{fee}} platform fee",
                    fee: `${(r.feeCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })} ${r.currency}`,
                  })}
                </div>
              </div>
            ))}
            <div>
              <div className="font-display text-3xl">{revenue.jobs}</div>
              <div className="text-[11px] text-muted-foreground mt-1">
                {t("app.pages.analytics.revenueJobs", { defaultValue: "paid jobs" })}
              </div>
            </div>
          </div>
        )}

        {revenue.pendingQuotes > 0 && (
          <div className="mt-3 text-xs text-muted-foreground">
            {t("app.pages.analytics.revenuePending", {
              defaultValue:
                "{{n}} quote(s) sent and not yet paid — not counted above, because they are not revenue until they are.",
              n: revenue.pendingQuotes,
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <DashboardShell
      title={t("app.pages.analytics.title")}
      subtitle={t("app.pages.analytics.subtitle")}
      actions={
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`text-xs px-3 py-1.5 rounded-full ${days === d ? "brand-gradient text-black" : "glass border border-border text-muted-foreground hover:bg-white/5"}`}
            >
              {d}d
            </button>
          ))}
        </div>
      }
    >
      <RevenueBlock revenue={data.revenue} days={days} />

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass-strong rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-1">
            {t("app.pages.analytics.conversations")}
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="c" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="oklch(0.82 0.18 168)" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="oklch(0.82 0.18 168)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="d"
                  stroke="oklch(0.65 0.02 260)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="oklch(0.65 0.02 260)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "#0F1117",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="conversations"
                  stroke="oklch(0.82 0.18 168)"
                  strokeWidth={2}
                  fill="url(#c)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="glass-strong rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-1">
            {t("app.pages.analytics.messages")}
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={series}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="d"
                  stroke="oklch(0.65 0.02 260)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="oklch(0.65 0.02 260)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "#0F1117",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  cursor={{ fill: "rgba(255,255,255,0.05)" }}
                />
                <Bar dataKey="messages" fill="oklch(0.70 0.18 240)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <div className="glass-strong rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-1">
            {t("app.pages.analytics.leads")}
          </div>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={series}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="d"
                  stroke="oklch(0.65 0.02 260)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="oklch(0.65 0.02 260)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "#0F1117",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  cursor={{ fill: "rgba(255,255,255,0.05)" }}
                />
                <Bar dataKey="leads" fill="oklch(0.70 0.19 290)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="glass-strong rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-3">
            {t("app.pages.analytics.topQuestions")}
          </div>
          <ul className="space-y-2 text-sm">
            {(data.top ?? []).length === 0 && (
              <li className="text-muted-foreground text-xs">
                {t("app.pages.analytics.noQuestions")}
              </li>
            )}
            {(data.top ?? []).map((tq: any, i: number) => (
              <li key={i} className="flex gap-2 items-start">
                <span className="text-[oklch(0.82_0.18_168)] font-mono text-xs mt-0.5">
                  {i + 1}
                </span>
                <span className="text-muted-foreground">{tq.question}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="glass-strong rounded-2xl p-5 mt-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="font-display text-lg font-semibold flex items-center gap-2">
            <BrainCircuit className="h-4 w-4 text-[oklch(0.68_0.19_290)]" />{" "}
            {t("app.pages.analytics.gaps")}
          </div>
          <span className="text-xs text-muted-foreground">{t("app.pages.analytics.gapsSub")}</span>
        </div>
        {(data.gaps ?? []).length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 flex items-center gap-2">
            <Check className="h-4 w-4 text-[oklch(0.82_0.18_168)]" />{" "}
            {t("app.pages.analytics.noGaps")}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {(data.gaps ?? []).map((g: any) => (
              <li key={g.id} className="py-3 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-52">
                  <div className="text-sm">{g.question}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {t("app.pages.analytics.asked", {
                      count: g.asked_count,
                      date: new Date(g.last_asked_at).toLocaleDateString(),
                    })}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Link
                    to="/knowledge"
                    className="rounded-full brand-gradient text-black px-3 py-1.5 text-xs font-medium"
                  >
                    {t("app.pages.analytics.addKnowledge")}
                  </Link>
                  <button
                    onClick={() => markResolved(g.id)}
                    className="rounded-full glass border border-border px-3 py-1.5 text-xs hover:bg-white/5"
                  >
                    {t("app.pages.analytics.covered")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AgentTraffic />
    </DashboardShell>
  );
}
